import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom';
import { tokens } from '../../design-system/tokens';
import { filterCatalog } from './docCatalog';
import { AppVersionLabel } from '../../components/AppVersionLabel';

const shellConsole: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minHeight: 0,
  minWidth: 0,
  background: tokens.semantic.surfaceSubtle,
};

const shellPublic: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100vh',
  minWidth: 0,
  background: tokens.semantic.surfaceSubtle,
};

const topBar: CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  padding: '12px 20px',
  background: 'var(--va-shell-gradient)',
  color: tokens.semantic.textOnDark,
  borderBottom: `1px solid ${tokens.semantic.borderOnDark}`,
};

const body: CSSProperties = {
  flex: 1,
  display: 'flex',
  minHeight: 0,
  overflow: 'hidden',
};

const aside: CSSProperties = {
  width: 280,
  flexShrink: 0,
  overflowY: 'auto',
  borderRight: `1px solid ${tokens.semantic.border}`,
  background: tokens.semantic.surface,
  padding: '16px 14px',
};

const main: CSSProperties = {
  flex: 1,
  overflowY: 'auto',
  padding: '24px 28px 48px',
  background: tokens.semantic.surface,
};

const searchInp: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  padding: '10px 12px',
  borderRadius: 10,
  border: `1px solid ${tokens.semantic.border}`,
  fontSize: 14,
  marginBottom: 14,
  outline: 'none',
};

const navLinkStyle = (active: boolean): CSSProperties => ({
  display: 'block',
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 13,
  fontWeight: active ? 600 : 500,
  textDecoration: 'none',
  color: active ? tokens.colors.accent[700] : tokens.semantic.textSecondary,
  background: active ? tokens.colors.accent[50] : 'transparent',
  marginBottom: 2,
});

type Props = { variant: 'public' | 'console' };

export function DocsShell({ variant }: Props) {
  const loc = useLocation();
  const [q, setQ] = useState('');
  const filtered = useMemo(() => filterCatalog(q), [q]);

  const sections = useMemo(() => {
    const names = [...new Set(filtered.map((d) => d.section))];
    return names.map((name) => ({
      name,
      items: filtered.filter((d) => d.section === name),
    }));
  }, [filtered]);

  const base = variant === 'console' ? '/app/docs' : '/docs';

  return (
    <div style={variant === 'console' ? shellConsole : shellPublic}>
      <header style={topBar}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
          <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-0.02em' }}>Documentación</span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: tokens.semantic.textOnDarkMuted,
              textTransform: 'uppercase',
              letterSpacing: '0.06em',
            }}
          >
            Virtual agents
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <AppVersionLabel onDark />
          {variant === 'console' ? (
            <Link
              to="/app"
              style={{ color: tokens.semantic.textOnDark, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
            >
              ← Consola
            </Link>
          ) : (
            <>
              <Link
                to="/login"
                style={{ color: tokens.semantic.textOnDark, fontSize: 14, fontWeight: 600, textDecoration: 'none' }}
              >
                Iniciar sesión
              </Link>
              <Link
                to="/"
                style={{ color: tokens.semantic.textOnDarkMuted, fontSize: 13, textDecoration: 'none' }}
              >
                Portada
              </Link>
            </>
          )}
        </div>
      </header>

      <div style={body}>
        <aside style={aside} aria-label="Índice de documentación">
          <label htmlFor="va-docs-search" style={{ fontSize: 11, fontWeight: 700, color: tokens.semantic.textMuted, textTransform: 'uppercase' }}>
            Buscar
          </label>
          <input
            id="va-docs-search"
            type="search"
            placeholder="Ej. conversaciones, calendario…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ ...searchInp, marginTop: 6 }}
            autoComplete="off"
          />
          <NavLink to={base} end style={{ fontSize: 13, fontWeight: 600, marginBottom: 12, display: 'block', color: tokens.colors.accent[600] }}>
            Índice general
          </NavLink>
          {sections.map((sec) => (
            <div key={sec.name} style={{ marginBottom: 18 }}>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: tokens.semantic.textMuted,
                  textTransform: 'uppercase',
                  marginBottom: 8,
                  letterSpacing: '0.04em',
                }}
              >
                {sec.name}
              </div>
              {sec.items.map((item) => {
                const to = `${base}/${item.area}/${item.slug}`;
                const active = loc.pathname === to;
                return (
                  <NavLink key={to} to={to} style={navLinkStyle(active)} title={item.keywords}>
                    {item.label}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </aside>
        <main style={main}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
